import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as React from 'react'

import { cn } from '@/lib/utils'

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    width={12}
    height={6}
    className={cn('fill-white drop-shadow-sm', className)}
    {...props}
  />
))
TooltipArrow.displayName = TooltipPrimitive.Arrow.displayName

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    /** Edunodes design-system tooltip: white card, 16px radius, caret, 0px offset by default. */
    variant?: 'default' | 'design'
  }
>(({ className, sideOffset, variant = 'default', children, ...props }, ref) => {
  const isDesign = variant === 'design'
  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset ?? (isDesign ? 0 : 4)}
      className={cn(
        'z-50 overflow-hidden text-sm animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]',
        isDesign
          ? 'max-w-[240px] rounded-2xl border border-[#e0e0e0] bg-white px-[18px] py-3 text-[14px] font-normal leading-5 text-[#1f1f1f] shadow-sm'
          : 'rounded-md border bg-popover px-3 py-1.5 text-popover-foreground shadow-md',
        className
      )}
      {...props}
    >
      {children}
      {isDesign ? <TooltipArrow /> : null}
    </TooltipPrimitive.Content>
  )
})
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * Click/press-to-toggle tooltip (Figma: pressed icon shows tooltip).
 * Collision-aware placement; 0px gap from trigger when using design variant.
 */
function PressTooltip({
  content,
  children,
  side,
  align = 'center',
  disabled = false,
}: {
  content: React.ReactNode
  children: React.ReactElement
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  disabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)

  if (disabled) {
    return children
  }

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip
        open={open}
        onOpenChange={next => {
          // Click-to-open only (Figma: press icon). Still allow dismiss on outside/escape.
          if (!next) setOpen(false)
        }}
      >
        <TooltipTrigger
          asChild
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            setOpen(prev => !prev)
          }}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent variant="design" side={side} align={align} collisionPadding={8}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export { PressTooltip, Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger }
